import React, { useEffect, useRef, useState } from 'react';
import Button from '../../../components/common/Button';
import { AgentChange, AgentPlanStatus, AgentPlanStep, AgentStepKind } from '../../../types/interfaces';
import DownloadButton from './DownloadButton';
import { PlanState, formatBytes } from './agentThread';

/** A rejected promise re-enables the buttons. */
type Action = Promise<unknown> | void;

export interface PlanCardProps {
  plan: PlanState;
  onApprove(): Action;
  onRevise(comment: string): Action;
  onCancel(): Action;
  busy: boolean;
  /** Set when the latest plan event had `reapproval: true`. */
  reapproval?: { reason: string | null } | null;
}

const OPS = ['insert', 'update', 'delete'] as const;

/** Steps whose effect the preview predicts rather than measures (spec §6). */
const PREDICTED_KINDS: AgentStepKind[] = ['api', 'minio_put', 'minio_delete'];

export const OUTCOME_UNKNOWN = 'Outcome unknown — check the audit log and the data before retrying.';

const STATUS_LINE: Partial<Record<AgentPlanStatus, string>> = {
  previewing: 'Building the preview…',
  approved: 'Approved; waiting to run.',
  running: 'Running…',
  done: 'Done.',
  failed: 'Failed.',
  cancelled: 'Cancelled.',
  superseded: 'Superseded by a revised plan.',
};

function show(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

function fields(row: Record<string, unknown> | null): string {
  if (!row) return '—';
  return Object.entries(row).map(([k, v]) => `${k}: ${show(v)}`).join(', ');
}

const OP_LABEL: Record<string, string> = { I: 'insert', U: 'update', D: 'delete' };

function SampleRow({ change }: { change: AgentChange }) {
  return (
    <tr className={`agent-sample--${(OP_LABEL[change.op] || 'other')}`}>
      <td>{change.table}</td>
      <td>{OP_LABEL[change.op] || change.op}</td>
      <td>{show(change.pk)}</td>
      <td>{fields(change.before)}</td>
      <td>{fields(change.after)}</td>
    </tr>
  );
}

function StepDetail({ step }: { step: AgentPlanStep }) {
  const parts: string[] = [];
  if (step.sql) parts.push(step.sql);
  if (step.params) parts.push(`-- params: ${JSON.stringify(step.params)}`);
  if (step.code) parts.push(step.code);
  if (step.method || step.path) parts.push(`${step.method || ''} ${step.path || ''}`.trim());
  if (step.body) parts.push(JSON.stringify(step.body, null, 2));
  if (step.prefix) parts.push(`prefix: ${step.prefix}`);
  if (step.keys && step.keys.length) parts.push(`keys: ${step.keys.join(', ')}`);
  if (step.inputs) parts.push(`inputs: ${JSON.stringify(step.inputs)}`);
  return (
    <div className="agent-plan__code-step">
      <div className="agent-meta">{step.id} · {step.kind} · {step.title}</div>
      <pre className="agent-code"><code>{parts.join('\n\n') || '(no code)'}</code></pre>
    </div>
  );
}

const PlanCard: React.FC<PlanCardProps> = ({ plan, onApprove, onRevise, onCancel, busy, reapproval }) => {
  const [clicked, setClicked] = useState(false);
  const [pending, setPending] = useState(false);
  const [revising, setRevising] = useState(false);
  const [comment, setComment] = useState('');
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const body = plan.plan;
  const preview = plan.preview;
  const tables = preview?.tables || {};
  const tiles = Object.entries(tables).flatMap(([table, counts]) =>
    OPS.filter((op) => (counts?.[op] || 0) > 0).map((op) => ({ table, op, count: counts[op] })));
  const minio = preview?.minio || [];
  const sample = preview?.sample || [];
  const steps = body?.steps || [];
  const writes = tiles.length > 0 || minio.length > 0;
  const skipped = body?.skipped_inputs || [];
  const externalSteps = (preview?.predicted || []).filter((p) => p.external);
  const externalText = preview?.external_effects?.length
    ? preview.external_effects
    : externalSteps.map((p) => `${p.id}: ${p.method || ''} ${p.path || ''}`.trim());
  const awaiting = plan.status === 'awaiting_approval';

  /** Runs an action; `onFail` runs when its promise rejects. */
  const run = (action: () => Action, onDone?: () => void, onFail?: () => void) => {
    setPending(true);
    let result: Action;
    try {
      result = action(); // synchronously, within the click
    } catch (err) {
      result = Promise.reject(err);
    }
    Promise.resolve(result)
      .then(() => { if (mounted.current) onDone?.(); }, () => { if (mounted.current) onFail?.(); })
      .finally(() => { if (mounted.current) setPending(false); });
  };

  const approve = () => {
    if (clicked || busy) return;
    setClicked(true);
    run(onApprove, undefined, () => setClicked(false));
  };

  const sendRevision = () => {
    const text = comment.trim();
    if (!text) return;
    run(() => onRevise(text), () => {
      setRevising(false);
      setComment('');
    });
  };

  const locked = busy || pending;
  const unknown = plan.outcome === 'unknown';

  return (
    <article className="agent-msg agent-msg--bot agent-plan" aria-label={`Plan ${plan.id}`}>
      <div className="agent-tags">
        <span className="agent-tag agent-tag--plan">Plan</span>
        {writes && <span className="agent-tag agent-tag--write">Writes data</span>}
        {unknown && <span className="agent-tag agent-tag--unknown">Outcome unknown</span>}
      </div>

      {reapproval && (
        <div className="agent-banner agent-banner--warning" role="alert" aria-label="Needs approval again">
          The data changed since you approved this plan, so it needs approving again.
          {reapproval.reason ? ` ${reapproval.reason}` : ''}
        </div>
      )}

      {body?.summary && <p className="agent-plan__summary">{body.summary}</p>}

      {steps.length > 0 && (
        <ol className="agent-plan__steps">
          {steps.map((step) => (
            <li key={step.id}>
              {step.title}
              {PREDICTED_KINDS.includes(step.kind) && (
                <span className="agent-tag agent-tag--predicted">predicted</span>
              )}
            </li>
          ))}
        </ol>
      )}

      {externalText.length > 0 && (
        <div className="agent-banner agent-banner--error" role="alert" aria-label="External effect">
          <strong>External effect:</strong> these steps reach people or systems outside the database and
          cannot be rolled back.
          <ul>{externalText.map((t) => <li key={t}>{t}</li>)}</ul>
        </div>
      )}

      {skipped.length > 0 && (
        <div className="agent-banner agent-banner--warning">
          {skipped.length} input {skipped.length === 1 ? 'row' : 'rows'} skipped:
          <ul>
            {skipped.map((s, i) => (
              <li key={i}>{s.row !== undefined ? `row ${show(s.row)}: ` : ''}{s.reason || show(s)}</li>
            ))}
          </ul>
        </div>
      )}

      {(tiles.length > 0 || skipped.length > 0) && (
        <div className="agent-counts">
          {tiles.map((t) => (
            <div key={`${t.table}-${t.op}`} className="agent-count" aria-label={`${t.table} ${t.op}: ${t.count}`}>
              <b>{t.count}</b>
              <span>{t.table} · {t.op}</span>
            </div>
          ))}
          {skipped.length > 0 && (
            <div className="agent-count" aria-label={`skipped: ${skipped.length}`}>
              <b>{skipped.length}</b><span>skipped</span>
            </div>
          )}
        </div>
      )}

      {minio.map((m) => (m.op === 'delete' ? (
        <div key={m.id} className="agent-banner agent-banner--warning">
          Deletes {m.count} {m.count === 1 ? 'object' : 'objects'} ({formatBytes(m.bytes)})
          {m.prefix ? <> under <code>{m.prefix}</code></> : null}
          {(m.sample_keys || []).length > 0 && (
            <ul className="agent-keys">{(m.sample_keys || []).map((k) => <li key={k}><code>{k}</code></li>)}</ul>
          )}
        </div>
      ) : (
        <div key={m.id} className={`agent-banner ${m.overwrite ? 'agent-banner--warning' : 'agent-banner--info'}`}>
          Uploads <code>{m.key}</code>
          {m.overwrite && <> — <strong>Overwrites the existing object</strong> ({formatBytes(m.existing_size)})</>}
        </div>
      )))}

      {preview && sample.length > 0 && (
        <>
          <div className="agent-meta">
            Before / after (sample of {preview.change_count} {preview.change_count === 1 ? 'change' : 'changes'})
          </div>
          <div className="agent-table-wrap">
            <table className="agent-table">
              <thead><tr><th>table</th><th>op</th><th>key</th><th>before</th><th>after</th></tr></thead>
              <tbody>{sample.map((c, i) => <SampleRow key={i} change={c} />)}</tbody>
            </table>
          </div>
        </>
      )}

      {preview?.diff_file_id ? (
        <div className="agent-plan__diff">
          <DownloadButton fileId={preview.diff_file_id}>Download full diff</DownloadButton>
        </div>
      ) : null}

      {steps.length > 0 && (
        <details className="agent-details">
          <summary>Exact SQL / code / API calls</summary>
          {steps.map((step) => <StepDetail key={step.id} step={step} />)}
        </details>
      )}

      {awaiting ? (
        <>
          <div className="agent-plan__buttons">
            <Button variant="primary" size="sm" onClick={approve} disabled={locked || clicked}>Approve &amp; run</Button>
            <Button variant="outline" size="sm" onClick={() => setRevising((v) => !v)} disabled={locked}>
              Revise
            </Button>
            <Button variant="ghost" size="sm" onClick={() => run(onCancel)} disabled={locked}>Cancel</Button>
          </div>
          {revising && (
            <div className="agent-plan__revise">
              <textarea
                className="input input--default input--md agent-plan__revise-input"
                aria-label="Revision comment"
                placeholder="What should change?"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
              />
              <Button variant="secondary" size="sm" onClick={sendRevision} disabled={locked || !comment.trim()}>
                Send revision
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="agent-plan__status" role="status">
          {unknown ? OUTCOME_UNKNOWN : (STATUS_LINE[plan.status] || plan.status)}
          {plan.status === 'approved' && plan.approved_by ? ` (by ${plan.approved_by})` : ''}
          {plan.status === 'approved' && (
            <Button variant="ghost" size="sm" onClick={() => run(onCancel)} disabled={locked}>Cancel</Button>
          )}
        </div>
      )}
    </article>
  );
};

export default PlanCard;
